<template>
  <b-card :title="isEdit ? 'แก้ไขสถานที่' : 'เพิ่มสถานที่'">
    <!-- แถวที่มาจาก OSM: บันทึกแล้วจะถูกล็อกไม่ให้ import ทับ -->
    <b-alert
      v-if="isEdit && input.source === 'osm'"
      variant="warning"
      show
      class="mb-2"
    >
      <div class="alert-body">
        ข้อมูลนี้มาจาก OpenStreetMap — เมื่อกดบันทึก ระบบจะล็อกไว้ไม่ให้การ import รอบถัดไปเขียนทับ
      </div>
    </b-alert>

    <b-form @submit.prevent="onSubmit">
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group label="หมวด">
            <b-form-select
              v-model="input.category"
              :options="categoryOptions"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="4"
        >
          <b-form-group label="ยี่ห้อ / เครือ">
            <b-form-input
              v-model="input.brand"
              placeholder="เช่น 7-Eleven"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="4"
        >
          <b-form-group label="สถานะ">
            <b-form-checkbox
              v-model="input.is_active"
              switch
            >
              เปิดใช้งาน
            </b-form-checkbox>
          </b-form-group>
        </b-col>

        <!-- ชื่อ / ที่อยู่ รายภาษา -->
        <b-col cols="12">
          <h5>ชื่อและที่อยู่</h5>
          <hr>
          <b-tabs>
            <b-tab
              v-for="lang in langs"
              :key="lang.code"
              :title="lang.name"
            >
              <b-row class="mt-1">
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group :label="`ชื่อ (${lang.name})`">
                    <b-form-input v-model="input.name[lang.code]" />
                  </b-form-group>
                </b-col>
                <b-col
                  cols="12"
                  md="6"
                >
                  <b-form-group :label="`ที่อยู่ (${lang.name})`">
                    <b-form-input v-model="input.address[lang.code]" />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>
          <small
            v-if="!hasAnyName"
            class="text-danger"
          >ต้องกรอกชื่ออย่างน้อยหนึ่งภาษา</small>
        </b-col>

        <b-col
          cols="12"
          class="mt-2"
        >
          <h5>พิกัด</h5>
          <hr>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="Latitude">
            <b-form-input
              v-model="input.lat"
              placeholder="13.7563"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="Longitude">
            <b-form-input
              v-model="input.lng"
              placeholder="100.5018"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="โทรศัพท์">
            <b-form-input v-model="input.phone" />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group label="เว็บไซต์">
            <b-form-input v-model="input.website" />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          class="mt-2 text-right"
        >
          <b-button
            variant="outline-secondary"
            class="mr-1"
            :to="{ name: 'poi-list' }"
          >
            ยกเลิก
          </b-button>
          <b-button
            type="submit"
            variant="primary"
            :disabled="isSubmitting"
          >
            บันทึก
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BRow, BCol,
  BButton, BTabs, BTab, BAlert,
} from 'bootstrap-vue'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import poiStoreModule from '../poiStoreModule'
import languagesStoreModule from '../../languages/languagesStoreModule'
import { POI_CATEGORIES } from '../poi-list/usePoiList'

/**
 * เพิ่ม / แก้ไข สถานที่ใกล้เคียง (POI)
 *
 * ชื่อและที่อยู่เก็บรายภาษาในตาราง poi_lang ฝั่ง API — ฟอร์มจึงเป็นแท็บภาษา
 * ภาษาที่เว้นว่างจะไม่ถูกบันทึก และถ้าเคยมีอยู่จะถูกลบ (API จัดการให้)
 *
 * ⚠️ กดบันทึกแล้ว API ตั้ง is_locked = 1 เสมอ — แถวที่มาจาก OSM จะไม่ถูก import ทับอีก
 */
export default {
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormCheckbox,
    BRow,
    BCol,
    BButton,
    BTabs,
    BTab,
    BAlert,
  },
  /*
   * ⚠️ ใช้ `root.$route` / `root.$router` แทนการ import ตัว router ตรง ๆ
   * ไฟล์ route ของหน้านี้ lazy-import คอมโพเนนต์นี้อยู่ การ import router กลับเข้ามา
   * จะกลายเป็น dependency cycle (eslint import/no-cycle ฟ้อง)
   */
  setup(props, { root }) {
    const toast = useToast()
    const POI_STORE_MODULE_NAME = 'app-poi'
    const LANGUAGES_STORE_MODULE_NAME = 'app-languages'

    if (!store.hasModule(POI_STORE_MODULE_NAME)) store.registerModule(POI_STORE_MODULE_NAME, poiStoreModule)
    if (!store.hasModule(LANGUAGES_STORE_MODULE_NAME)) store.registerModule(LANGUAGES_STORE_MODULE_NAME, languagesStoreModule)

    onUnmounted(() => {
      if (store.hasModule(POI_STORE_MODULE_NAME)) store.unregisterModule(POI_STORE_MODULE_NAME)
    })

    const { id } = root.$route.params
    const isEdit = computed(() => !!id)
    const isSubmitting = ref(false)
    const langs = ref([])

    const input = ref({
      category: 'school',
      name: {},
      address: {},
      brand: '',
      phone: '',
      website: '',
      lat: null,
      lng: null,
      is_active: true,
      source: 'manual',
    })

    const categoryOptions = POI_CATEGORIES

    const hasAnyName = computed(() => Object.values(input.value.name).some(v => String(v || '').trim()))

    const notify = (title, variant) => {
      toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'danger' ? 'AlertTriangleIcon' : 'CheckIcon', variant },
      })
    }

    store.dispatch('app-languages/fetchData', { is_active: 1 })
      .then(res => { if (res.data.success) langs.value = res.data.data })

    if (id) {
      store.dispatch('app-poi/fetchDetail', id)
        .then(res => {
          const record = res.data.data

          if (!record) return

          input.value = {
            category: record.category,
            name: { ...record.name },
            address: { ...record.address },
            brand: record.brand || '',
            phone: record.phone || '',
            website: record.website || '',
            lat: record.lat,
            lng: record.lng,
            is_active: Number(record.is_active) === 1,
            source: record.source,
          }
        })
        .catch(() => notify('โหลดข้อมูลไม่สำเร็จ', 'danger'))
    }

    const onSubmit = () => {
      if (!hasAnyName.value) {
        notify('ต้องกรอกชื่ออย่างน้อยหนึ่งภาษา', 'danger')

        return
      }

      const lat = Number(input.value.lat)
      const lng = Number(input.value.lng)

      /* พิกัดเป็นตัวหลักของฟีเจอร์นี้ (ใช้หาสถานที่ใกล้คอนโด) ว่างหรือเป็น 0 ไม่ได้ */
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) {
        notify('ใส่ Latitude และ Longitude ให้ถูกต้อง', 'danger')

        return
      }

      isSubmitting.value = true

      const payload = {
        category: input.value.category,
        name: input.value.name,
        address: input.value.address,
        lat,
        lng,
        brand: input.value.brand || null,
        phone: input.value.phone || null,
        website: input.value.website || null,
        is_active: input.value.is_active ? 1 : 0,
      }

      const action = id
        ? store.dispatch('app-poi/updateData', { id, payload })
        : store.dispatch('app-poi/addData', payload)

      action
        .then(res => {
          if (!res.data.success) {
            notify(res.data.message || 'บันทึกไม่สำเร็จ', 'danger')

            return
          }

          notify('บันทึกแล้ว', 'success')
          root.$router.push({ name: 'poi-list' })
        })
        .catch(error => {
          const message = error && error.response && error.response.data ? error.response.data.message : null

          notify(message || 'บันทึกไม่สำเร็จ', 'danger')
        })
        .finally(() => { isSubmitting.value = false })
    }

    return {
      isEdit,
      isSubmitting,
      langs,
      input,
      categoryOptions,
      hasAnyName,
      onSubmit,
    }
  },
}
</script>
